import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Tooltip, Tabs } from 'antd';
import { useAppStore } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';

// ─── AntD icon data imports ───────────────────────────────────────────────────
/* eslint-disable @typescript-eslint/no-var-requires */
// Arrows
const RightSvg        = require('@ant-design/icons-svg/lib/asn/RightOutlined').default;
const CaretRightSvg   = require('@ant-design/icons-svg/lib/asn/CaretRightOutlined').default;
const RightCircleSvg  = require('@ant-design/icons-svg/lib/asn/RightCircleOutlined').default;
const SwapRightSvg    = require('@ant-design/icons-svg/lib/asn/SwapRightOutlined').default;
const ArrowRightSvg   = require('@ant-design/icons-svg/lib/asn/ArrowRightOutlined').default;
const RightSquareSvg  = require('@ant-design/icons-svg/lib/asn/RightSquareOutlined').default;
const LoginSvg        = require('@ant-design/icons-svg/lib/asn/LoginOutlined').default;
const LogoutSvg       = require('@ant-design/icons-svg/lib/asn/LogoutOutlined').default;
const SendSvg         = require('@ant-design/icons-svg/lib/asn/SendOutlined').default;
const RollbackSvg     = require('@ant-design/icons-svg/lib/asn/RollbackOutlined').default;
const EnterSvg        = require('@ant-design/icons-svg/lib/asn/EnterOutlined').default;
const SyncSvg         = require('@ant-design/icons-svg/lib/asn/SyncOutlined').default;
// Symbols
const StarFilledSvg       = require('@ant-design/icons-svg/lib/asn/StarFilled').default;
const HeartFilledSvg      = require('@ant-design/icons-svg/lib/asn/HeartFilled').default;
const CrownFilledSvg      = require('@ant-design/icons-svg/lib/asn/CrownFilled').default;
const LikeFilledSvg       = require('@ant-design/icons-svg/lib/asn/LikeFilled').default;
const DislikeFilledSvg    = require('@ant-design/icons-svg/lib/asn/DislikeFilled').default;
const TrophyFilledSvg     = require('@ant-design/icons-svg/lib/asn/TrophyFilled').default;
const GiftFilledSvg       = require('@ant-design/icons-svg/lib/asn/GiftFilled').default;
const FlagFilledSvg       = require('@ant-design/icons-svg/lib/asn/FlagFilled').default;
const TagFilledSvg        = require('@ant-design/icons-svg/lib/asn/TagFilled').default;
const BellFilledSvg       = require('@ant-design/icons-svg/lib/asn/BellFilled').default;
// Objects
const FireFilledSvg        = require('@ant-design/icons-svg/lib/asn/FireFilled').default;
const ThunderboltFilledSvg = require('@ant-design/icons-svg/lib/asn/ThunderboltFilled').default;
const SunFilledSvg         = require('@ant-design/icons-svg/lib/asn/SunFilled').default;
const MoonFilledSvg        = require('@ant-design/icons-svg/lib/asn/MoonFilled').default;
const CloudFilledSvg       = require('@ant-design/icons-svg/lib/asn/CloudFilled').default;
const RocketFilledSvg      = require('@ant-design/icons-svg/lib/asn/RocketFilled').default;
const BulbFilledSvg        = require('@ant-design/icons-svg/lib/asn/BulbFilled').default;
const SmileFilledSvg       = require('@ant-design/icons-svg/lib/asn/SmileFilled').default;
const MehFilledSvg         = require('@ant-design/icons-svg/lib/asn/MehFilled').default;
const FrownFilledSvg       = require('@ant-design/icons-svg/lib/asn/FrownFilled').default;
const CompassFilledSvg     = require('@ant-design/icons-svg/lib/asn/CompassFilled').default;
const EnvironmentFilledSvg = require('@ant-design/icons-svg/lib/asn/EnvironmentFilled').default;
const AlertFilledSvg       = require('@ant-design/icons-svg/lib/asn/AlertFilled').default;
const ExperimentFilledSvg  = require('@ant-design/icons-svg/lib/asn/ExperimentFilled').default;
/* eslint-enable @typescript-eslint/no-var-requires */

// ─── Preset stamp generators ──────────────────────────────────────────────────

type Painter = (ctx: CanvasRenderingContext2D, size: number) => void;

interface IconNode {
  tag: string;
  attrs: Record<string, string | number | boolean>;
  children?: IconNode[];
}

function paintFromIcon(iconData: { icon: IconNode }, ctx: CanvasRenderingContext2D, size: number) {
  const scale = size / 896;
  ctx.save();
  ctx.fillStyle = 'white';
  ctx.scale(scale, scale);
  ctx.translate(-64, -64);
  function draw(node: IconNode) {
    if (node.tag === 'path') {
      const d = node.attrs.d as string;
      if (d) ctx.fill(new Path2D(d));
    }
    node.children?.forEach(draw);
  }
  draw(iconData.icon);
  ctx.restore();
}

// Canvas helpers for geometric shapes
function polygon(ctx: CanvasRenderingContext2D, s: number, sides: number, rotation = -Math.PI / 2) {
  const cx = s / 2, cy = s / 2, r = s * 0.44;
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = (i * 2 * Math.PI) / sides + rotation;
    if (i === 0) ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
    else         ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  ctx.closePath(); ctx.fill();
}

function star5(ctx: CanvasRenderingContext2D, s: number) {
  const cx = s / 2, cy = s / 2, spikes = 5, outer = s * 0.45, inner = s * 0.19;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const a = (i * Math.PI) / spikes - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    if (i === 0) ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
    else         ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  ctx.closePath(); ctx.fill();
}

interface PresetGroup { key: string; label: string; items: { label: string; paint: Painter }[]; }

const PRESET_GROUPS: PresetGroup[] = [
  {
    key: 'shapes', label: 'Shapes',
    items: [
      { label: 'Circle',     paint: (ctx, s) => { ctx.fillStyle = 'white'; ctx.beginPath(); ctx.arc(s / 2, s / 2, s * 0.44, 0, Math.PI * 2); ctx.fill(); } },
      { label: 'Ring',       paint: (ctx, s) => { const r = s / 2; ctx.fillStyle = 'white'; ctx.beginPath(); ctx.arc(r, r, r * 0.9, 0, Math.PI * 2); ctx.fill(); ctx.globalCompositeOperation = 'destination-out'; ctx.beginPath(); ctx.arc(r, r, r * 0.58, 0, Math.PI * 2); ctx.fill(); ctx.globalCompositeOperation = 'source-over'; } },
      { label: 'Square',     paint: (ctx, s) => { ctx.fillStyle = 'white'; const m = s * 0.06; ctx.fillRect(m, m, s - m * 2, s - m * 2); } },
      { label: 'Heart',      paint: (ctx, s) => paintFromIcon(HeartFilledSvg, ctx, s) },
      { label: 'Triangle ▲', paint: (ctx, s) => { ctx.fillStyle = 'white'; polygon(ctx, s, 3); } },
      { label: 'Thunderbolt', paint: (ctx, s) => paintFromIcon(ThunderboltFilledSvg, ctx, s) },
      { label: 'Pentagon',   paint: (ctx, s) => { ctx.fillStyle = 'white'; polygon(ctx, s, 5); } },
      { label: 'Hexagon',    paint: (ctx, s) => { ctx.fillStyle = 'white'; polygon(ctx, s, 6, 0); } },
      { label: 'Octagon',    paint: (ctx, s) => { ctx.fillStyle = 'white'; polygon(ctx, s, 8, Math.PI / 8); } },
      { label: 'Plus',       paint: (ctx, s) => { ctx.fillStyle = 'white'; const t = s * 0.32, b = s * 0.68, e = s * 0.05; ctx.beginPath(); ctx.moveTo(t, e); ctx.lineTo(b, e); ctx.lineTo(b, t); ctx.lineTo(s - e, t); ctx.lineTo(s - e, b); ctx.lineTo(b, b); ctx.lineTo(b, s - e); ctx.lineTo(t, s - e); ctx.lineTo(t, b); ctx.lineTo(e, b); ctx.lineTo(e, t); ctx.lineTo(t, t); ctx.closePath(); ctx.fill(); } },
      { label: 'Star 5',     paint: (ctx, s) => { ctx.fillStyle = 'white'; star5(ctx, s); } },
      { label: 'Star 6',     paint: (ctx, s) => { ctx.fillStyle = 'white'; const cx = s/2, cy = s/2, spikes = 6, outer = s*0.45, inner = s*0.22; ctx.beginPath(); for (let i = 0; i < spikes*2; i++) { const a = (i*Math.PI)/spikes - Math.PI/2; const r = i%2===0 ? outer : inner; if (i===0) ctx.moveTo(cx+r*Math.cos(a), cy+r*Math.sin(a)); else ctx.lineTo(cx+r*Math.cos(a), cy+r*Math.sin(a)); } ctx.closePath(); ctx.fill(); } },
    ],
  },
  {
    key: 'arrows', label: 'Arrows',
    items: [
      { label: 'Right',         paint: (ctx, s) => paintFromIcon(RightSvg, ctx, s) },
      { label: 'Caret',         paint: (ctx, s) => paintFromIcon(CaretRightSvg, ctx, s) },
      { label: 'Right Circle',  paint: (ctx, s) => paintFromIcon(RightCircleSvg, ctx, s) },
      { label: 'Swap Right',    paint: (ctx, s) => paintFromIcon(SwapRightSvg, ctx, s) },
      { label: 'Arrow Right',   paint: (ctx, s) => paintFromIcon(ArrowRightSvg, ctx, s) },
      { label: 'Right Square',  paint: (ctx, s) => paintFromIcon(RightSquareSvg, ctx, s) },
      { label: 'Login',         paint: (ctx, s) => paintFromIcon(LoginSvg, ctx, s) },
      { label: 'Logout',        paint: (ctx, s) => paintFromIcon(LogoutSvg, ctx, s) },
      { label: 'Send',          paint: (ctx, s) => paintFromIcon(SendSvg, ctx, s) },
      { label: 'Rollback',      paint: (ctx, s) => paintFromIcon(RollbackSvg, ctx, s) },
      { label: 'Enter',         paint: (ctx, s) => paintFromIcon(EnterSvg, ctx, s) },
      { label: 'Sync',          paint: (ctx, s) => paintFromIcon(SyncSvg, ctx, s) },
    ],
  },
  {
    key: 'symbols', label: 'Symbols',
    items: [
      { label: 'Star',    paint: (ctx, s) => paintFromIcon(StarFilledSvg, ctx, s) },
      { label: 'Heart',   paint: (ctx, s) => paintFromIcon(HeartFilledSvg, ctx, s) },
      { label: 'Crown',   paint: (ctx, s) => paintFromIcon(CrownFilledSvg, ctx, s) },
      { label: 'Like',    paint: (ctx, s) => paintFromIcon(LikeFilledSvg, ctx, s) },
      { label: 'Dislike', paint: (ctx, s) => paintFromIcon(DislikeFilledSvg, ctx, s) },
      { label: 'Trophy',  paint: (ctx, s) => paintFromIcon(TrophyFilledSvg, ctx, s) },
      { label: 'Gift',    paint: (ctx, s) => paintFromIcon(GiftFilledSvg, ctx, s) },
      { label: 'Flag',    paint: (ctx, s) => paintFromIcon(FlagFilledSvg, ctx, s) },
      { label: 'Tag',     paint: (ctx, s) => paintFromIcon(TagFilledSvg, ctx, s) },
      { label: 'Bell',    paint: (ctx, s) => paintFromIcon(BellFilledSvg, ctx, s) },
      { label: 'Rocket',  paint: (ctx, s) => paintFromIcon(RocketFilledSvg, ctx, s) },
      { label: 'Bulb',    paint: (ctx, s) => paintFromIcon(BulbFilledSvg, ctx, s) },
    ],
  },
  {
    key: 'objects', label: 'Objects',
    items: [
      { label: 'Fire',        paint: (ctx, s) => paintFromIcon(FireFilledSvg, ctx, s) },
      { label: 'Thunderbolt', paint: (ctx, s) => paintFromIcon(ThunderboltFilledSvg, ctx, s) },
      { label: 'Sun',         paint: (ctx, s) => paintFromIcon(SunFilledSvg, ctx, s) },
      { label: 'Moon',        paint: (ctx, s) => paintFromIcon(MoonFilledSvg, ctx, s) },
      { label: 'Cloud',       paint: (ctx, s) => paintFromIcon(CloudFilledSvg, ctx, s) },
      { label: 'Compass',     paint: (ctx, s) => paintFromIcon(CompassFilledSvg, ctx, s) },
      { label: 'Location',    paint: (ctx, s) => paintFromIcon(EnvironmentFilledSvg, ctx, s) },
      { label: 'Alert',       paint: (ctx, s) => paintFromIcon(AlertFilledSvg, ctx, s) },
      { label: 'Experiment',  paint: (ctx, s) => paintFromIcon(ExperimentFilledSvg, ctx, s) },
      { label: 'Smile',       paint: (ctx, s) => paintFromIcon(SmileFilledSvg, ctx, s) },
      { label: 'Meh',         paint: (ctx, s) => paintFromIcon(MehFilledSvg, ctx, s) },
      { label: 'Frown',       paint: (ctx, s) => paintFromIcon(FrownFilledSvg, ctx, s) },
    ],
  },
];

const PRESET_SIZE = 128;

function makePresetCanvas(paint: Painter, size = PRESET_SIZE): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  paint(ctx, size);
  return c;
}

// Per-group cache of rendered preset canvases
const presetCaches: (HTMLCanvasElement | null)[][] =
  PRESET_GROUPS.map((g) => g.items.map(() => null));

function getGroupCanvas(groupIdx: number, itemIdx: number): HTMLCanvasElement {
  if (!presetCaches[groupIdx][itemIdx]) {
    presetCaches[groupIdx][itemIdx] = makePresetCanvas(PRESET_GROUPS[groupIdx].items[itemIdx].paint);
  }
  return presetCaches[groupIdx][itemIdx]!;
}

// ─── Thumbnail grid item ──────────────────────────────────────────────────────

interface ThumbProps {
  canvas: HTMLCanvasElement;
  label: string;
  selected: boolean;
  onClick: () => void;
}
const Thumb: React.FC<ThumbProps> = ({ canvas, label, selected, onClick }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ctx = el.getContext('2d')!;
    ctx.clearRect(0, 0, 64, 64);
    // Checkerboard bg
    for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) {
      ctx.fillStyle = (r + c) % 2 === 0 ? '#444' : '#666';
      ctx.fillRect(c * 16, r * 16, 16, 16);
    }
    ctx.drawImage(canvas, 0, 0, 64, 64);
  }, [canvas]);

  return (
    <Tooltip title={label}>
      <canvas
        ref={ref}
        width={64}
        height={64}
        onClick={onClick}
        style={{
          cursor: 'pointer',
          borderRadius: 4,
          border: selected ? '2px solid #558cf4' : '2px solid transparent',
          display: 'block',
        }}
      />
    </Tooltip>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const StampPickerModal: React.FC<Props> = ({ open, onClose }) => {
  const setStampImage = useAppStore((s) => s.setStampImage);
  const setStampIsPreset = useAppStore((s) => s.setStampIsPreset);

  const [activeTab, setActiveTab] = useState<string>(PRESET_GROUPS[0].key);
  const [selectedPreset, setSelectedPreset] = useState<{ tab: string; idx: number } | null>(
    { tab: PRESET_GROUPS[0].key, idx: 0 }
  );
  const [customCanvases, setCustomCanvases] = useState<{ canvas: HTMLCanvasElement; label: string }[]>([]);
  const [selectedCustom, setSelectedCustom] = useState<number | null>(null);

  const dropRef = useRef<HTMLDivElement>(null);
  const [draggingOver, setDraggingOver] = useState(false);

  // ── Load image from File ────────────────────────────────────────────────────
  const MAX_STAMP_SIZE = 512;
  const loadFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_STAMP_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      const label = file.name.replace(/\.[^.]+$/, '');
      setCustomCanvases((prev) => [...prev, { canvas: c, label }]);
      const idx = customCanvases.length;
      setSelectedCustom(idx);
      setSelectedPreset(null);
      setStampImage(c);
      setStampIsPreset(false);
    };
    img.src = url;
  }, [customCanvases.length, setStampImage]);

  // ── Drag and drop ───────────────────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDraggingOver(true); };
  const onDragLeave = () => setDraggingOver(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) loadFile(file);
  };

  // ── File input ──────────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadFile(file);
    e.target.value = '';
  };

  // ── Clipboard paste ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onPaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) loadFile(blob);
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, loadFile]);

  // ── Select preset ───────────────────────────────────────────────────────────
  const pickPreset = (tabKey: string, groupIdx: number, itemIdx: number) => {
    setSelectedPreset({ tab: tabKey, idx: itemIdx });
    setSelectedCustom(null);
    setStampImage(getGroupCanvas(groupIdx, itemIdx));
    setStampIsPreset(true);
  };

  // ── Apply / close ───────────────────────────────────────────────────────────
  const handleOk = () => onClose();

  const tabItems = PRESET_GROUPS.map((group, gi) => ({
    key: group.key,
    label: group.label,
    children: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 64px)', gap: 8 }}>
        {group.items.map((item, ii) => (
          <Thumb
            key={ii}
            canvas={getGroupCanvas(gi, ii)}
            label={item.label}
            selected={selectedPreset?.tab === group.key && selectedPreset?.idx === ii && selectedCustom === null}
            onClick={() => pickPreset(group.key, gi, ii)}
          />
        ))}
      </div>
    ),
  }));

  return (
    <Modal
      title="Select Stamp"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="Use Stamp"
      width={500}
    >
      {/* Preset tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        items={tabItems}
        style={{ marginBottom: 4 }}
        indicator={{ size: 20, align: 'start'}}
      />

      {/* Custom images */}
      {customCanvases.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>CUSTOM</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 64px)', gap: 8 }}>
            {customCanvases.map((c, i) => (
              <Thumb
                key={i}
                canvas={c.canvas}
                label={c.label}
                selected={selectedCustom === i}
                onClick={() => { setSelectedCustom(i); setSelectedPreset(null); setStampImage(c.canvas); setStampIsPreset(false); }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div
        ref={dropRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: draggingOver ? '2px dashed #558cf4' : '2px dashed #444',
          borderRadius: 8,
          padding: '20px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          color: draggingOver ? '#558cf4' : '#666',
          fontSize: 13,
          background: draggingOver ? 'rgba(85,140,244,0.08)' : 'transparent',
          transition: 'all 0.15s',
        }}
      >
        Drop an image here, click to browse, or <kbd style={{ padding: '1px 4px', background: '#2a2a2a', border: '1px solid #555', borderRadius: 3 }}>Ctrl+V</kbd> to paste as stamp
      </div>
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Tooltip title="Paste the clipboard image directly onto the selected layer (same as Ctrl+V outside the stamp tool)" placement="top">
          <button
            onClick={() => { viewportActions.pasteClipboard.current(); onClose(); }}
            style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
              background: 'transparent', border: '1px solid #555', color: '#888',
            }}
          >
            Paste as Layer…
          </button>
        </Tooltip>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />
    </Modal>
  );
};

export default StampPickerModal;
